import { useState, useEffect, useMemo, useCallback } from 'react';
import { FileText, CreditCard, Clock, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { useAuth } from '../../context/AuthContext';
import { Link } from 'react-router-dom';
import Pagination from '../../components/ui/Pagination';

const ITEMS_PER_PAGE = 10;

export default function ClientQuotes() {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [payingQuote, setPayingQuote] = useState(null);
  const [acceptedCGV, setAcceptedCGV] = useState({});
  const [currentPage, setCurrentPage] = useState(1);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const paginatedQuotes = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return quotes.slice(start, start + ITEMS_PER_PAGE);
  }, [quotes, currentPage]);

  const totalPages = Math.ceil(quotes.length / ITEMS_PER_PAGE);

  const fetchQuotes = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/payments/my-quotes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setQuotes(data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token]);

  useEffect(() => {
    fetchQuotes();
  }, [fetchQuotes]);

  const handleCGVChange = (quoteId, checked) => {
    setAcceptedCGV(prev => ({ ...prev, [quoteId]: checked }));
  };

  const handlePayDeposit = async (quoteId) => {
    if (!acceptedCGV[quoteId]) return;
    setPayingQuote(quoteId);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout/quote/${quoteId}/deposit`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setPayingQuote(null);
    }
  };

  const handlePayMidway = null; // Removed - no longer needed

  const handlePayFull = async (quoteId) => {
    if (!acceptedCGV[quoteId]) return;
    setPayingQuote(quoteId);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout/quote/${quoteId}/full`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setPayingQuote(null);
    }
  };

  const handlePayFinal = async (quoteId) => {
    if (!acceptedCGV[quoteId]) return;
    setPayingQuote(quoteId);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout/quote/${quoteId}/final`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setPayingQuote(null);
    }
  };

  const handlePayKlarna = async (quoteId) => {
    if (!acceptedCGV[quoteId]) return;
    setPayingQuote(quoteId);
    try {
      const res = await fetch(`${API_URL}/api/payments/checkout/quote/${quoteId}/klarna`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setPayingQuote(null);
    }
  };

  const StatusBadge = ({ status }) => {
    const styles = {
      paid: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
      pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
      deposit_paid: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      midway_paid: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
      cancelled: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
      expired: 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
    };
    const labels = {
      paid: 'Payé intégralement',
      pending: 'En attente de paiement',
      deposit_paid: 'Acompte versé',
      midway_paid: 'Acompte versé',
      cancelled: 'Annulé',
      expired: 'Expiré'
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const PaymentProgress = ({ quote }) => {
    const total = quote.amount;
    
    if (quote.payment_type === 'full') {
      return (
        <div className="mt-4 p-4 bg-muted/50 rounded-lg">
          <h4 className="font-medium mb-3 text-sm">Paiement</h4>
          <div className="space-y-2">
            <div className="flex justify-between items-center text-sm">
              <span className={quote.status === 'paid' ? 'text-green-600' : ''}>
                {quote.status === 'paid' ? '✓' : '○'} Paiement intégral
              </span>
              <span className="font-medium">{total}€</span>
            </div>
          </div>
        </div>
      );
    }
    
    const depositAmount = quote.deposit_amount || Math.round(total * 0.30 * 100) / 100;
    const finalAmount = quote.final_amount || Math.round(total * 0.70 * 100) / 100;
    
    return (
      <div className="mt-4 p-4 bg-muted/50 rounded-lg">
        <h4 className="font-medium mb-3 text-sm">Échéancier de paiement</h4>
        <div className="space-y-2">
          <div className="flex justify-between items-center text-sm">
            <span className={quote.deposit_paid ? 'text-green-600' : ''}>
              {quote.deposit_paid ? '✓' : '○'} Acompte (30%)
            </span>
            <span className="font-medium">{depositAmount}€</span>
          </div>
          <div className="flex justify-between items-center text-sm">
            <span className={quote.final_paid ? 'text-green-600' : ''}>
              {quote.final_paid ? '✓' : '○'} Solde (70%)
            </span>
            <span className="font-medium">{finalAmount}€</span>
          </div>
          <div className="border-t pt-2 mt-2 flex justify-between items-center font-semibold">
            <span>Total</span>
            <span>{total}€</span>
          </div>
        </div>
      </div>
    );
  };

  const CGVCheckbox = ({ quoteId }) => (
    <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
      <Checkbox 
        id={`cgv-${quoteId}`}
        checked={acceptedCGV[quoteId] || false}
        onCheckedChange={(checked) => handleCGVChange(quoteId, checked)}
      />
      <label htmlFor={`cgv-${quoteId}`} className="text-sm cursor-pointer">
        J'accepte les{' '}
        <Link to="/cgv" target="_blank" className="text-primary underline hover:no-underline">
          Conditions Générales de Vente
        </Link>
        {' '}et les{' '}
        <Link to="/mentions-legales" target="_blank" className="text-primary underline hover:no-underline">
          Mentions Légales
        </Link>
      </label>
    </div>
  );

  const PaymentButtons = ({ quote }) => {
    const isCGVAccepted = acceptedCGV[quote.id];
    const isPaying = payingQuote === quote.id;
    const total = quote.amount;

    if (quote.status === 'paid') return null;

    // Full payment type - single payment
    if (quote.payment_type === 'full') {
      return (
        <div className="space-y-4 mt-4">
          <CGVCheckbox quoteId={quote.id} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Button 
              onClick={() => handlePayFull(quote.id)}
              disabled={!isCGVAccepted || isPaying}
              className="btn-primary"
            >
              <CreditCard className="mr-2 h-4 w-4" />
              {isPaying ? 'Redirection...' : `Régler (${total}€)`}
            </Button>
            <Button 
              variant="outline"
              onClick={() => handlePayKlarna(quote.id)}
              disabled={!isCGVAccepted || isPaying}
            >
              Payer en 3x avec Klarna ({total}€)
            </Button>
          </div>
        </div>
      );
    }

    // Deposit type - first payment (30%)
    if (!quote.deposit_paid && !quote.uses_klarna) {
      const depositAmount = Math.round(total * 0.30 * 100) / 100;
      return (
        <div className="space-y-4 mt-4">
          <CGVCheckbox quoteId={quote.id} />
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Paiement par acompte</p>
              <Button 
                onClick={() => handlePayDeposit(quote.id)}
                disabled={!isCGVAccepted || isPaying}
                className="w-full btn-primary"
              >
                <CreditCard className="mr-2 h-4 w-4" />
                {isPaying ? 'Redirection...' : `Verser l'acompte (${depositAmount}€)`}
              </Button>
            </div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Ou paiement en 3x sans frais</p>
              <Button 
                variant="outline"
                onClick={() => handlePayKlarna(quote.id)}
                disabled={!isCGVAccepted || isPaying}
                className="w-full"
              >
                Payer en 3x avec Klarna ({total}€)
              </Button>
            </div>
          </div>
        </div>
      );
    }

    // Deposit paid - ready for final (70%)
    if (quote.deposit_paid && !quote.final_paid) {
      const finalAmount = quote.final_amount || Math.round(total * 0.70 * 100) / 100;
      return (
        <div className="space-y-4 mt-4">
          <CGVCheckbox quoteId={quote.id} />
          <Button 
            onClick={() => handlePayFinal(quote.id)}
            disabled={!isCGVAccepted || isPaying}
            className="btn-primary"
          >
            <CreditCard className="mr-2 h-4 w-4" />
            {isPaying ? 'Redirection...' : `Régler le solde (${finalAmount}€)`}
          </Button>
        </div>
      );
    }

    return null;
  };

  if (loading) {
    return <div className="animate-pulse h-64 bg-muted rounded-xl" />;
  }

  return (
    <div className="space-y-8" data-testid="client-quotes-page">
      <div>
        <h1 className="text-3xl font-bold mb-2">Mes devis</h1>
        <p className="text-muted-foreground">Consultez et payez vos devis</p>
      </div>

      {quotes.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">Aucun devis</h3>
            <p className="text-muted-foreground">
              Vous n'avez pas encore de devis. Contactez-nous pour obtenir un devis personnalisé.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {paginatedQuotes.map((quote) => (
            <Card key={quote.id} className="overflow-hidden" data-testid={`quote-card-${quote.id}`}>
              <CardContent className="p-6">
                <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                  <div className="flex items-start gap-4 flex-1">
                    <div className={`p-3 rounded-xl shrink-0 ${
                      quote.status === 'paid' 
                        ? 'bg-green-100 dark:bg-green-900/30' 
                        : quote.deposit_paid 
                          ? 'bg-blue-100 dark:bg-blue-900/30'
                          : 'bg-primary/10'
                    }`}>
                      {quote.status === 'paid' ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      ) : quote.deposit_paid ? (
                        <AlertCircle className="h-6 w-6 text-blue-600" />
                      ) : (
                        <Clock className="h-6 w-6 text-primary" />
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{quote.title}</h3>
                      {quote.description && (
                        <div className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{quote.description}</div>
                      )}
                      {quote.items && quote.items.length > 0 && (
                        <div className="mt-3 rounded-lg border border-border/60 overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/50">
                              <tr>
                                <th className="text-left px-3 py-2 font-semibold">Désignation</th>
                                <th className="text-right px-3 py-2 font-semibold w-24">Qté</th>
                                <th className="text-right px-3 py-2 font-semibold w-28">PU HT</th>
                                <th className="text-right px-3 py-2 font-semibold w-28">Total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {quote.items.map((it, idx) => (
                                <tr key={idx} className="border-t border-border/40">
                                  <td className="px-3 py-2">
                                    <div className="font-medium">{it.name}</div>
                                    {it.description && (
                                      <div className="text-xs text-muted-foreground mt-0.5 whitespace-pre-line">{it.description}</div>
                                    )}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {Number(it.quantity) || 0}{it.unit && it.unit !== 'forfait' ? ` ${it.unit}` : ''}
                                  </td>
                                  <td className="px-3 py-2 text-right">{Number(it.unit_price || 0).toFixed(2)} €</td>
                                  <td className="px-3 py-2 text-right font-medium">{((Number(it.quantity) || 0) * (Number(it.unit_price) || 0)).toFixed(2)} €</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-2">
                        Créé le {new Date(quote.created_at).toLocaleDateString('fr-FR')}
                        {quote.valid_until && quote.status === 'pending' && (
                          <span className="ml-2 text-orange-600 dark:text-orange-400">
                            • Valide jusqu'au {new Date(quote.valid_until).toLocaleDateString('fr-FR')}
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-bold">{quote.amount}€</div>
                    <StatusBadge status={quote.status} />
                  </div>
                </div>

                {/* Payment progress and buttons */}
                {quote.status !== 'paid' && !quote.uses_klarna && (
                  <PaymentProgress quote={quote} />
                )}
                
                <PaymentButtons quote={quote} />
              </CardContent>
            </Card>
          ))}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            totalItems={quotes.length}
            itemsPerPage={ITEMS_PER_PAGE}
          />
        </div>
      )}
    </div>
  );
}
